import React, { useState } from 'react';
import { useGetMyPublishedQuery, MyPublishedBlog } from '../store/topicApi';
import Pagination from './Pagination';

const ITEMS_PER_PAGE = 9;

const categoryColor = (cat?: string) => {
  const map: Record<string, string> = {
    QA: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    DevOps: 'bg-trident-orange-light text-trident-orange border-trident-orange/30',
    'AI/ML': 'bg-purple-100 text-purple-700 border-purple-200',
    Development: 'bg-blue-100 text-blue-700 border-blue-200',
    Security: 'bg-red-100 text-red-700 border-red-200',
    Architecture: 'bg-sky-100 text-sky-700 border-sky-200',
    'Power Platform': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'Business Analysis': 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return map[cat || ''] || 'bg-trident-gray-100 text-trident-gray-700 border-trident-gray-200';
};

const BlogCard: React.FC<{ blog: MyPublishedBlog }> = ({ blog }) => (
  <a
    href={blog.publishedUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="group bg-white rounded-2xl border border-trident-gray-100 hover:border-trident-orange/40 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col"
  >
    {blog.featuredImage && (
      <div className="aspect-video overflow-hidden bg-trident-gray-100">
        <img src={blog.featuredImage} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
    )}
    <div className="p-5 flex-1 flex flex-col">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {blog.category && (
          <span className={`inline-flex text-[10px] font-display font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${categoryColor(blog.category)}`}>
            {blog.category}
          </span>
        )}
        {blog.readTime && (
          <span className="text-[10px] text-trident-gray-500 font-display font-semibold">
            · {blog.readTime}
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-base text-trident-gray-900 leading-snug mb-2 line-clamp-2 group-hover:text-trident-orange transition-colors">
        {blog.title}
      </h3>
      {blog.excerpt && (
        <p className="text-sm text-trident-gray-500 line-clamp-3 mb-4 flex-1">{blog.excerpt}</p>
      )}
      <div className="flex items-center justify-between pt-3 border-t border-trident-gray-100 text-xs text-trident-gray-500">
        <span>{blog.publishedDate || '—'}</span>
        <div className="flex items-center gap-3">
          {blog.backlinkCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              {blog.backlinkCount}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-trident-orange font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
            View
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </span>
        </div>
      </div>
    </div>
  </a>
);

const MyPublishedBlogs: React.FC = () => {
  const { data: blogs = [], isLoading, error } = useGetMyPublishedQuery();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(blogs.length / ITEMS_PER_PAGE);
  const paginated = blogs.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Failed to load blogs.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">My Published Blogs</h2>
        <p className="text-sm text-trident-gray-500">Articles you've written that are live on the ITTrident site.</p>
      </div>

      {blogs.length === 0 ? (
        <div className="card text-center py-16">
          <div className="w-16 h-16 rounded-full bg-trident-orange-light text-trident-orange flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          </div>
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">No published blogs yet</h3>
          <p className="text-sm text-trident-gray-500">Your published articles will appear here.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {paginated.map((b) => <BlogCard key={b._id} blog={b} />)}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={blogs.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};

export default MyPublishedBlogs;
